import React, { useState } from 'react';
import {
  AlertCircle, AlertTriangle, CheckCircle, Zap, ChevronDown, ChevronRight,
  ExternalLink, Plus, RefreshCw, Loader2, X, ArrowRight, FileText,
  Users, Shield, DollarSign, Briefcase
} from 'lucide-react';
import { CriticalGap, ImprovementOpportunity, ProposalStrength } from '../../types/source-grounded';

interface GapResolutionCenterProps {
  criticalGaps: CriticalGap[];
  improvementOpportunities: ImprovementOpportunity[];
  strengths: ProposalStrength[];
  onFixGap: (gapIndex: number, solution: string) => Promise<void>;
  onFixAll: () => Promise<void>;
  onNavigateToProfile: (section: string) => void;
}

type SolutionType = 'add-to-profile' | 'reframe-existing' | 'generate-content' | 'find-partner';

interface GapSolution {
  type: SolutionType;
  label: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
}

export function GapResolutionCenter({
  criticalGaps,
  improvementOpportunities,
  strengths,
  onFixGap,
  onFixAll,
  onNavigateToProfile,
}: GapResolutionCenterProps) {
  const [expandedGap, setExpandedGap] = useState<string | null>(null);
  const [fixingGap, setFixingGap] = useState<string | null>(null);
  const [fixingAll, setFixingAll] = useState(false);

  const handleFixGap = async (gapIndex: number, solution: string) => {
    const gapId = `critical-${gapIndex}`;
    setFixingGap(gapId);
    try {
      await onFixGap(gapIndex, solution);
    } finally {
      setFixingGap(null);
    }
  };

  const handleFixAll = async () => {
    setFixingAll(true);
    try {
      await onFixAll();
    } finally {
      setFixingAll(false);
    }
  };

  const getSolutionsForGap = (gap: CriticalGap, index: number): GapSolution[] => {
    const profileSection = extractProfileSection(gap.profileAction);

    return [
      {
        type: 'add-to-profile',
        label: 'Add to Profile',
        description: `Update your company profile to address this requirement`,
        icon: <Plus className="h-4 w-4" />,
        action: () => onNavigateToProfile(profileSection),
      },
      {
        type: 'reframe-existing',
        label: 'Reframe Existing',
        description: 'AI will rewrite using existing data differently',
        icon: <RefreshCw className="h-4 w-4" />,
        action: () => handleFixGap(index, 'reframe'),
      },
      {
        type: 'generate-content',
        label: 'Generate Content',
        description: 'AI will create new content to address this gap',
        icon: <Zap className="h-4 w-4" />,
        action: () => handleFixGap(index, 'generate'),
      },
      {
        type: 'find-partner',
        label: 'Find Teaming Partner',
        description: 'Suggest partnering approach for this requirement',
        icon: <Users className="h-4 w-4" />,
        action: () => handleFixGap(index, 'partner'),
      },
    ];
  };

  const extractProfileSection = (profileAction: string | undefined): string => {
    if (!profileAction) return 'company';

    const action = profileAction.toLowerCase();
    if (action.includes('certification')) return 'certifications';
    if (action.includes('past performance')) return 'past-performance';
    if (action.includes('personnel')) return 'personnel';
    if (action.includes('pricing')) return 'pricing';
    return 'company';
  };

  const getCategoryIcon = (gap: CriticalGap | ImprovementOpportunity) => {
    const text = ('requirement' in gap ? gap.requirement : gap.currentState || '').toLowerCase();

    if (text.includes('certif') || text.includes('compliance')) {
      return <Shield className="h-4 w-4" />;
    }
    if (text.includes('personnel') || text.includes('staff')) {
      return <Users className="h-4 w-4" />;
    }
    if (text.includes('pric') || text.includes('cost')) {
      return <DollarSign className="h-4 w-4" />;
    }
    if (text.includes('past performance') || text.includes('experience')) {
      return <Briefcase className="h-4 w-4" />;
    }
    return <FileText className="h-4 w-4" />;
  };

  const totalIssues = criticalGaps.length + improvementOpportunities.length;

  return (
    <div className="space-y-6">
      {/* Header with Fix All Button */}
      {totalIssues > 0 && (
        <div className="flex items-center justify-between bg-bw-surface-subtle rounded-lg p-4 border border-red-200">
          <div>
            <h3 className="font-semibold text-bw-ink">
              {totalIssues} Issue{totalIssues > 1 ? 's' : ''} Found
            </h3>
            <p className="text-sm text-bw-muted">
              {criticalGaps.length} critical, {improvementOpportunities.length} improvements
            </p>
          </div>
          <button
            onClick={handleFixAll}
            disabled={fixingAll}
            className="flex items-center px-4 py-2 bg-bw-sidebar text-white rounded-lg hover:from-servet-blue/90 hover:to-servet-dark-blue/90 disabled:opacity-50 transition-all"
          >
            {fixingAll ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            Fix All with AI
          </button>
        </div>
      )}

      {/* Critical Gaps Section */}
      {criticalGaps.length > 0 && (
        <div className="space-y-3">
          <h4 className="flex items-center text-sm font-semibold text-urgent">
            <AlertCircle className="h-4 w-4 mr-2" />
            Critical Gaps
            <span className="ml-2 px-2 py-0.5 bg-urgent-bg text-urgent rounded-full text-xs">
              {criticalGaps.length}
            </span>
          </h4>
          <p className="text-xs text-bw-muted">
            These issues may result in proposal disqualification
          </p>

          <div className="space-y-2">
            {criticalGaps.map((gap, index) => {
              const gapId = `critical-${index}`;
              const isExpanded = expandedGap === gapId;
              const isFixing = fixingGap === gapId;
              const solutions = getSolutionsForGap(gap, index);

              return (
                <div
                  key={gapId}
                  className="bg-urgent-bg border border-urgent-border rounded-lg overflow-hidden"
                >
                  {/* Gap Header */}
                  <button
                    onClick={() => setExpandedGap(isExpanded ? null : gapId)}
                    className="w-full flex items-start p-3 text-left hover:bg-urgent-bg/50 transition-colors"
                  >
                    <div className="flex-shrink-0 mt-0.5 text-urgent">
                      {getCategoryIcon(gap)}
                    </div>
                    <div className="flex-1 ml-3 min-w-0">
                      <p className="text-sm font-medium text-bw-ink">
                        {gap.requirement}
                      </p>
                      {gap.requirementSource && (
                        <p className="text-xs text-bw-muted mt-0.5">
                          Source: {gap.requirementSource}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center ml-2">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-bw-muted" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-bw-muted" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-red-200 bg-bw-surface">
                      {/* Impact */}
                      <div className="mt-3 p-2 bg-urgent-bg rounded text-xs text-urgent">
                        <strong>Impact:</strong> {gap.impact}
                      </div>

                      {/* Recommendation */}
                      {gap.recommendation && (
                        <div className="mt-2 text-sm text-bw-ink">
                          <strong className="text-xs text-bw-muted">AI Recommendation:</strong>
                          <p className="mt-1">{gap.recommendation}</p>
                        </div>
                      )}

                      {/* Solutions */}
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-medium text-bw-muted">Choose a solution:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {solutions.map((solution) => (
                            <button
                              key={solution.type}
                              onClick={solution.action}
                              disabled={isFixing}
                              className="flex items-center p-2 text-left border border-bw-border rounded-lg hover:border-servet-blue hover:bg-bw-primary-tint transition-colors disabled:opacity-50 text-xs"
                            >
                              <span className="flex-shrink-0 text-bw-primary">
                                {isFixing && fixingGap === gapId ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  solution.icon
                                )}
                              </span>
                              <span className="ml-2 font-medium text-bw-ink">
                                {solution.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Improvement Opportunities Section */}
      {improvementOpportunities.length > 0 && (
        <div className="space-y-3">
          <h4 className="flex items-center text-sm font-semibold text-fit-moderate">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Improvement Opportunities
            <span className="ml-2 px-2 py-0.5 bg-fit-moderate-bg text-fit-moderate rounded-full text-xs">
              {improvementOpportunities.length}
            </span>
          </h4>
          <p className="text-xs text-bw-muted">
            Addressing these will improve your evaluation score
          </p>

          <div className="space-y-2">
            {improvementOpportunities.map((opp, index) => {
              const oppId = `improve-${index}`;
              const isExpanded = expandedGap === oppId;

              return (
                <div
                  key={oppId}
                  className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedGap(isExpanded ? null : oppId)}
                    className="w-full flex items-start p-3 text-left hover:bg-fit-moderate-bg/50 transition-colors"
                  >
                    <div className="flex-shrink-0 mt-0.5 text-fit-moderate">
                      {getCategoryIcon(opp)}
                    </div>
                    <div className="flex-1 ml-3 min-w-0">
                      <p className="text-sm font-medium text-bw-ink">
                        {opp.requirement}
                      </p>
                      <p className="text-xs text-bw-muted mt-0.5">
                        {opp.currentState}
                      </p>
                    </div>
                    <div className="flex items-center ml-2">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-bw-muted" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-bw-muted" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-yellow-200 bg-bw-surface">
                      {/* Impact */}
                      <div className="mt-3 p-2 bg-fit-moderate-bg rounded text-xs text-fit-moderate">
                        <strong>Win Impact:</strong> {opp.impactOnWinProbability}
                      </div>

                      {/* Recommendation */}
                      <div className="mt-2 text-sm text-bw-ink">
                        <strong className="text-xs text-bw-muted">Recommendation:</strong>
                        <p className="mt-1">{opp.recommendation}</p>
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={() => handleFixGap(index, 'improve')}
                        className="mt-3 flex items-center px-3 py-1.5 bg-yellow-600 text-white rounded text-xs hover:bg-yellow-700"
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        Fix with AI
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Strengths Section */}
      {strengths.length > 0 && (
        <div className="space-y-3">
          <h4 className="flex items-center text-sm font-semibold text-success">
            <CheckCircle className="h-4 w-4 mr-2" />
            Strengths to Highlight
            <span className="ml-2 px-2 py-0.5 bg-success-bg text-success rounded-full text-xs">
              {strengths.length}
            </span>
          </h4>

          <div className="space-y-2">
            {strengths.map((strength, index) => (
              <div
                key={`strength-${index}`}
                className="bg-success-bg border border-success-border rounded-lg p-3"
              >
                <p className="text-sm font-medium text-bw-ink">
                  {strength.strength}
                </p>
                {strength.suggestedUsage && (
                  <p className="text-xs text-bw-muted mt-1">
                    <strong>Use in:</strong> {strength.suggestedUsage}
                  </p>
                )}
                <div className="mt-2 flex items-center">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    strength.relevance === 'HIGH' ? 'bg-green-200 text-success' :
                    strength.relevance === 'MEDIUM' ? 'bg-blue-200 text-bw-primary' :
                    'bg-gray-200 text-bw-ink'
                  }`}>
                    {strength.relevance} Relevance
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Issues State */}
      {totalIssues === 0 && strengths.length === 0 && (
        <div className="text-center py-8">
          <CheckCircle className="h-12 w-12 text-success mx-auto mb-3" />
          <h4 className="font-medium text-bw-ink">No Issues Found</h4>
          <p className="text-sm text-bw-muted mt-1">
            Your proposal is looking good! Run a full analysis for detailed feedback.
          </p>
        </div>
      )}
    </div>
  );
}
